/**
 * Escolha do perfil de compartilhamento de tela.
 *
 * A escolha real não é "qualidade alta ou baixa": é o que sacrificar quando a
 * rede aperta. Por isso os cartões falam de conteúdo, e não de números.
 *
 * O que a plataforma gasta de banda não aparece aqui. Quem vai mostrar a tela
 * está decidindo entre nitidez e fluidez, não administrando um servidor; conta
 * de consumo é assunto do painel de administração, e no meio de uma conversa
 * ela só cria uma preocupação que não é de quem está lendo.
 */

import { useEffect, useState } from 'react'
import { AppWindow, Check, Gamepad2, Gauge, Monitor, Sparkles, Video, Volume2 } from 'lucide-react'
import { SCREEN_SHARE_PRESETS, type ScreenSharePresetId } from '@nexus/shared'
import { cn } from '@/lib/cn'
import { Modal, Select } from '@/components/ui'
import { usePreferencesStore } from '@/store/preferences'
import { isDesktop, listCaptureSources, type DesktopSource } from '@/lib/desktop'
import type { FonteDeTransmissao } from '@/store/voice'
import {
  listarDispositivosDeVideo,
  pedirPermissaoDeVideo,
  type DispositivoDeVideo,
  type EntradaDeSom,
} from '@/lib/dispositivos-de-video'

const ICONS: Record<ScreenSharePresetId, typeof Monitor> = {
  gamer: Gamepad2,
  sharp: Sparkles,
  smooth: Monitor,
  economy: Gauge,
}

export function ScreenSharePicker({
  open,
  onClose,
  onSelect,
  defaultPreset,
}: {
  open: boolean
  onClose: () => void
  onSelect: (preset: ScreenSharePresetId, fonte: FonteDeTransmissao) => void
  defaultPreset: ScreenSharePresetId
}) {
  const setVideo = usePreferencesStore((s) => s.setVideo)

  /*
    Entradas de vídeo que dá para transmitir no lugar da tela.

    O caso que pediu isso é a placa de captura: ela entra no computador como se
    fosse uma webcam, e sem esta lista a única forma de mostrá-la era ligar a
    câmera, que tem um teto de qualidade cinco vezes menor. Um console ou um
    segundo computador ficavam borrados por uma limitação que não tinha nada a
    ver com eles.
  */
  const [dispositivos, setDispositivos] = useState<DispositivoDeVideo[]>([])
  const [entradasDeSom, setEntradasDeSom] = useState<EntradaDeSom[]>([])
  const [semRotulos, setSemRotulos] = useState(false)
  const [escolhido, setEscolhido] = useState<string | null>(null)

  /*
    Escolha manual do som.

    O vínculo automático depende de o navegador agrupar imagem e som do mesmo
    aparelho, o que costuma acontecer mas não é garantido: placa antiga, driver
    genérico ou entrada de som separada quebram o agrupamento. Sem esta escolha,
    quando o vínculo falha não sobra caminho nenhum, e a transmissão sai muda
    sem explicação.

    `null` quer dizer "o que foi vinculado sozinho"; a string vazia é a recusa
    explícita de som.
  */
  const [somManual, setSomManual] = useState<string | null>(null)

  /*
    Monitores e janelas, no aplicativo instalado.

    No navegador esta lista não existe e nem faz falta: ele mostra o seletor
    dele na hora da captura. No Electron não há seletor nenhum na maioria dos
    sistemas, e sem esta escolha todo compartilhamento saía como o monitor
    inteiro, sem jeito de mostrar só uma janela.
  */
  const [telas, setTelas] = useState<DesktopSource[]>([])
  const [telaEscolhida, setTelaEscolhida] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return

    let cancelado = false
    void listarDispositivosDeVideo().then((resultado) => {
      if (cancelado) return
      setDispositivos(resultado.dispositivos)
      setEntradasDeSom(resultado.entradasDeSom)
      setSemRotulos(resultado.dispositivos.length > 0 && !resultado.temRotulos)
    })

    void listCaptureSources().then((fontes) => {
      if (cancelado || !fontes) return
      setTelas(fontes)
      // Começa no primeiro monitor: é o que a pessoa espera ao abrir, e deixar
      // sem escolha nenhuma faria o botão de transmitir parecer quebrado.
      setTelaEscolhida((atual) => atual ?? fontes.find((f) => f.kind === 'screen')?.id ?? null)
    })

    return () => {
      cancelado = true
    }
  }, [open])

  // Ao fechar, a escolha volta ao padrão: a próxima transmissão começa do zero.
  useEffect(() => {
    if (!open) {
      setEscolhido(null)
      setSomManual(null)
      setTelaEscolhida(null)
    }
  }, [open])

  const revelarNomes = async () => {
    if (await pedirPermissaoDeVideo()) {
      const resultado = await listarDispositivosDeVideo()
      setDispositivos(resultado.dispositivos)
      setEntradasDeSom(resultado.entradasDeSom)
      setSemRotulos(false)
    }
  }

  const dispositivoEscolhido = dispositivos.find((d) => d.deviceId === escolhido)

  const somEfetivo = somManual === null ? dispositivoEscolhido?.audioDeviceId : somManual || undefined

  const fonte: FonteDeTransmissao = dispositivoEscolhido
    ? {
        tipo: 'dispositivo',
        deviceId: dispositivoEscolhido.deviceId,
        audioDeviceId: somEfetivo,
      }
    : { tipo: 'tela', sourceId: telaEscolhida ?? undefined }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Compartilhar tela"
      description={
        dispositivoEscolhido
          ? somEfetivo
            ? 'A transmissão vem do dispositivo escolhido, com imagem e som.'
            : 'A transmissão vem do dispositivo escolhido, sem som.'
          : telas.length > 0
            ? 'Escolha o que mostrar e depois o perfil de qualidade.'
            : 'Escolha o perfil. Você seleciona a janela ou o monitor na próxima etapa.'
      }
      size="xl"
    >
      {telas.length > 0 && !dispositivoEscolhido ? (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-content-primary">O que você quer mostrar</h3>
          <p className="mt-1 text-[13px] leading-relaxed text-content-secondary">
            Um monitor inteiro ou só a janela de um programa. Mostrar uma janela só evita que
            uma notificação ou uma aba aberta apareça sem querer para todo mundo.
          </p>

          <div className="scroll-region mt-3 grid max-h-64 grid-cols-2 gap-2 overflow-y-auto pr-1 sm:grid-cols-3 lg:grid-cols-4">
            {telas.map((tela) => (
              <button
                key={tela.id}
                type="button"
                onClick={() => setTelaEscolhida(tela.id)}
                aria-pressed={telaEscolhida === tela.id}
                title={tela.name}
                className={cn(
                  'group overflow-hidden rounded-lg border text-left transition-colors',
                  telaEscolhida === tela.id
                    ? 'border-brand bg-brand-subtle'
                    : 'border-line bg-surface-base hover:border-line-strong',
                )}
              >
                {/* A miniatura é o que faz a escolha ser rápida: nome de janela
                    costuma ser o título do documento, e várias ficam iguais. */}
                <div className="relative aspect-video bg-surface-deepest">
                  {tela.thumbnail ? (
                    <img
                      src={tela.thumbnail}
                      alt=""
                      className="size-full object-contain"
                      draggable={false}
                    />
                  ) : null}

                  {telaEscolhida === tela.id ? (
                    <span className="absolute top-1.5 right-1.5 flex size-5 items-center justify-center rounded-full bg-brand">
                      <Check className="size-3 text-white" />
                    </span>
                  ) : null}
                </div>

                <div className="flex items-center gap-1.5 px-2 py-1.5">
                  <span className="shrink-0 text-content-muted [&>svg]:size-3.5">
                    {tela.kind === 'screen' ? <Monitor /> : <AppWindow />}
                  </span>
                  {tela.appIcon ? (
                    <img src={tela.appIcon} alt="" className="size-3.5 shrink-0" />
                  ) : null}
                  <span className="truncate text-[11px] text-content-secondary">{tela.name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {SCREEN_SHARE_PRESETS.map((preset) => {
          const Icon = ICONS[preset.id]
          const isDefault = preset.id === defaultPreset

          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => onSelect(preset.id, fonte)}
              className={cn(
                'group relative flex flex-col gap-2.5 rounded-xl border p-5 text-left transition-all',
                'border-line bg-surface-base hover:border-brand hover:bg-surface-overlay',
              )}
            >
              {isDefault ? (
                <span className="absolute top-3 right-3 flex size-5 items-center justify-center rounded-full bg-brand">
                  <Check className="size-3 text-white" />
                </span>
              ) : null}

              <Icon className="size-6 text-brand" />

              <div>
                <h3 className="text-[15px] font-semibold text-content-primary">{preset.label}</h3>
                <p className="mt-1 text-[13px] leading-relaxed text-content-secondary">
                  {preset.hint}
                </p>
              </div>

              <dl className="mt-auto space-y-1 border-t border-line pt-2.5 text-[11px] text-content-muted">
                <div className="flex justify-between gap-2">
                  <dt>Resolução</dt>
                  <dd className="tabular-nums">
                    {preset.width}×{preset.height}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt>Taxa</dt>
                  <dd className="tabular-nums">{preset.framerate} fps</dd>
                </div>
              </dl>
            </button>
          )
        })}
      </div>

      <label className="mt-5 flex cursor-pointer items-center gap-2 text-[13px] text-content-secondary">
        <input
          type="checkbox"
          className="size-4 accent-[var(--color-brand)]"
          onChange={(e) => {
            if (e.target.checked) setVideo({ defaultScreenPreset: defaultPreset })
          }}
          defaultChecked
        />
        Lembrar minha escolha
      </label>

      {dispositivos.length > 0 ? (
        <div className="mt-7 border-t border-line pt-6">
          <h3 className="text-sm font-semibold text-content-primary">
            Ou transmita um dispositivo de vídeo
          </h3>
          <p className="mt-1 text-[13px] leading-relaxed text-content-secondary">
            Placa de captura, câmera externa ou qualquer entrada de vídeo. Vai com a mesma
            qualidade de uma transmissão de tela, e não a da câmera. Quando o aparelho também
            entrega som, ele vai junto.
          </p>

          {semRotulos ? (
            <button
              type="button"
              onClick={() => void revelarNomes()}
              className="mt-3 text-[13px] text-brand hover:underline"
            >
              Mostrar os nomes dos dispositivos
            </button>
          ) : null}

          <div className="mt-4 flex flex-wrap gap-2">
            <Opcao
              ativa={escolhido === null}
              onClick={() => setEscolhido(null)}
              icone={<Monitor />}
            >
              Minha tela
            </Opcao>

            {dispositivos.map((dispositivo) => (
              <Opcao
                key={dispositivo.deviceId}
                ativa={escolhido === dispositivo.deviceId}
                onClick={() => setEscolhido(dispositivo.deviceId)}
                icone={dispositivo.audioDeviceId ? <Volume2 /> : <Video />}
              >
                {dispositivo.label}
                {dispositivo.audioDeviceId ? (
                  <span className="ml-1.5 text-[11px] opacity-70">com som</span>
                ) : null}
              </Opcao>
            ))}
          </div>

          {dispositivoEscolhido && entradasDeSom.length > 0 ? (
            <div className="mt-4 max-w-sm">
              <label className="mb-1.5 block text-[13px] text-content-secondary">
                Som da transmissão
              </label>
              <Select
                value={somEfetivo ?? ''}
                onChange={(e) => setSomManual(e.target.value)}
              >
                <option value="">Sem som</option>
                {entradasDeSom.map((entrada) => (
                  <option key={entrada.deviceId} value={entrada.deviceId}>
                    {entrada.label}
                    {entrada.deviceId === dispositivoEscolhido.audioDeviceId
                      ? ' (do mesmo aparelho)'
                      : ''}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  )
}

function Opcao({
  ativa,
  onClick,
  icone,
  children,
}: {
  ativa: boolean
  onClick: () => void
  icone: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={ativa}
      className={cn(
        'flex max-w-full items-center gap-2 rounded-lg border px-3 py-2 text-[13px] transition-colors',
        ativa
          ? 'border-brand bg-brand-subtle text-content-primary'
          : 'border-line bg-surface-base text-content-secondary hover:border-line-strong',
      )}
    >
      <span className="shrink-0 [&>svg]:size-4">{icone}</span>
      {/* O nome de uma placa de captura costuma ser comprido e cheio de código
          do fabricante; cortar é melhor do que esticar o botão pela tela. */}
      <span className="truncate">{children}</span>
    </button>
  )
}
